import { Log } from '../../AuxillaryService/Log';
import { getQuotesForType } from '../../ReachApiV1';
import { TemplateWidgetMixin } from '../../Template';
import { shuffleArray } from '../../Utility/Shuffle';
import { FadeInCommand, FadeOutCommand, WidgetType } from '../types';
import { WidgetWithScheduler } from '../WidgetWithScheduler';
import {
    QuoteLayoutType,
    QuoteTemplateData,
    QuoteTemplateItemData,
    QuoteType,
    QuoteWidgetConfiguration,
    QuoteWidgetConfigurationDetails,
    QuoteWidgetTimelineSlot,
    QuoteWidgetTimelineSlotObject,
    ZodQuoteWidgetConfigurationDetails,
} from './types';

export class QuoteWidget extends TemplateWidgetMixin(WidgetWithScheduler) {
    readonly type: WidgetType.QUOTE = WidgetType.QUOTE;
    declare readonly config: QuoteWidgetConfigurationDetails;

    /**
     * Number of seconds between each time the `Scheduler` runs.
     * Overrides default to 20 minutes.
     */
    schedulerRunInterval = 1200;
    /**
     * Value to check against to ensure that the data is not updated
     *   too early.
     */
    #initialRun = Date.now();
    /**
     * Quick access variable for quoteType.
     */
    readonly #quoteType: string;
    /**
     * Total number of quotes assigned to this widget.
     */
    readonly #numberOfQuotes: number;
    /**
     * Array of quotes to cycle through.
     */
    #quotes: QuoteTemplateItemData[] = [];
    /**
     * Set of quote ids used to determine if it fetched new quotes.
     */
    #uniqueQuoteIds: Set<number> = new Set();
    /**
     * Has the quotes been changed since last render.
     */
    #quotesChanged = false;
    /**
     * What is the layout type of the quote.
     * Seems to only allow landscape currently.
     */
    #layoutType: QuoteLayoutType;
    /**
     * Helps decide which quote should be displayed.
     */
    #currentQuoteIndex = 0;

    constructor(widgetConfiguration: QuoteWidgetConfiguration, zoneId: number) {
        super(widgetConfiguration, zoneId);
        this.#quoteType = widgetConfiguration.config.quote_type;
        this.#numberOfQuotes = widgetConfiguration.config.number_of_quotes;
        this.#layoutType = this.getLayoutType();
    }

    protected validateConfigurationDetails(
        maybeQuoteWidgetConfigurationDetails: QuoteWidgetConfigurationDetails
    ): QuoteWidgetConfigurationDetails {
        return ZodQuoteWidgetConfigurationDetails.parse(maybeQuoteWidgetConfigurationDetails);
    }

    /**
     * Initialize Widget
     *
     * NOTE: Eagerly initialize since zone will not start until all widgets in zone
     *      are initialized Don't want one widget failure to stop all activity in
     *      zone
     */
    async initialize(): Promise<void> {
        await super.initialize();
        try {
            /**
             * Manually run checkBack to initially load the data.
             */
            await this.checkBack();
        } catch (err) {
            Log.Error(
                `Failed to initialize quotes in ${this.type} widget: ${this.name}`,
                null,
                __fileline,
                err
            );
        }
    }

    /**
     * Calls renderContent so the content reloads
     */
    async getReadyForDisplay(currentSlot: QuoteWidgetTimelineSlot): Promise<void> {
        if (currentSlot) {
            await this.renderContent();
        }
    }

    /**
     * Every `schedulerRunInterval` this will be run to update the quotes displayed.
     */
    async schedulerAction(): Promise<void> {
        /**
         * Wait until at least the minimum refresh time has been awaited
         *   to get new data.
         */
        if (this.schedulerRunInterval < Math.floor((Date.now() - this.#initialRun) / 1000)) {
            await this.checkBack();
        }
    }

    /**
     * Get the updated quotes and map them to the display.
     */
    async checkBack(): Promise<void> {
        const apiDataJq = await getQuotesForType(this.#quoteType, this.#numberOfQuotes);

        this.mapQuoteObjects(apiDataJq.quote_data);
    }

    /**
     * Map quote object to template properties.
     * If new quote exists, set quotesChanged to true to indicate update of DOM
     * required
     * @param quotes - array containing quotes
     */
    mapQuoteObjects(
        quotes: Array<{
            author: string;
            id: number;
            quote: string;
            title: string;
        }>
    ): void {
        const hasNewQuote = quotes.some((quote) => {
            return !this.#uniqueQuoteIds.has(quote.id);
        });

        if (hasNewQuote) {
            this.#quotesChanged = true;
            this.#quotes = [];
            this.#uniqueQuoteIds.clear();
            const imageLocations = this.getBackgroundImageLocations(this.#quoteType);
            let pageIndex = 0;

            quotes.forEach((quote) => {
                const tempQuoteObject: QuoteTemplateItemData = {
                    author: quote.author,
                    background_link: imageLocations[pageIndex],
                    id: quote.id,
                    title: quote.title,
                    quote: quote.quote,
                };

                pageIndex++;
                if (pageIndex === imageLocations.length) {
                    pageIndex = 0;
                }

                this.#quotes.push(tempQuoteObject);
                this.#uniqueQuoteIds.add(quote.id);
            });
        }
    }

    /**
     * Get image location to use for the background for the app, changes based on  type
     * @param type - type of quote
     * @return shuffled array of image urls
     */
    getBackgroundImageLocations(type: string): string[] {
        const imageLocations = [];
        let numberOfImages = 5;

        /**
         * TODO: Make this logic more generic based off of some setting or
         *   configurable setting, rather than hard coded.
         */
        if (type === QuoteType.FAMOUS || type === QuoteType.INSPIRATIONAL) {
            numberOfImages = 10;
        }

        for (let i = 1; i < numberOfImages + 1; i++) {
            imageLocations.push(
                `/images/quote_backgrounds/v1/${type}/${this.#layoutType}` +
                    `/${type}_${this.#layoutType}_${i}.jpg`
            );
        }

        return shuffleArray(imageLocations);
    }

    /**
     * Triggered once fade out of widget is complete
     * @param currentSlot
     * @returns Promise that resolves when the currentSlot is hidden
     */
    afterFadeOut(currentSlot: QuoteWidgetTimelineSlot): void {
        try {
            const element = this.wrapperElement.querySelector<HTMLElement>(
                `#quote-${this.id}-${currentSlot.params.quote.id}`
            );

            if (element) {
                element.style.left = '-9999999px';
                element.style.visibility = 'hidden';
            }
        } catch (err) {
            Log.Error(`Failed to unload quote widget: ${this.id}`, null, __fileline, err);
        }
    }

    /**
     * Triggered before fade in occurs
     * @param currentSlot
     * @returns Promise that resolves when current slot becomes visible
     */
    async beforeFadeIn(
        currentSlot: QuoteWidgetTimelineSlot
    ): Promise<Partial<FadeInCommand> | void> {
        try {
            if (this.#quotesChanged) {
                await this.renderContent();
                this.#quotesChanged = false;
            }

            const element = this.wrapperElement.querySelector<HTMLElement>(
                `#quote-${this.id}-${currentSlot.params.quote.id}`
            );

            if (element) {
                element.style.left = '0px';
                element.style.visibility = 'visible';
            }

            /**
             * Signal to re-make the loop.
             */
            if (this.#quotesChanged) {
                return {
                    abortLoop: true,
                };
            }
        } catch (err) {
            Log.Error(`Failed to load quote widget: ${this.id}`, null, __fileline, err);
        }
    }

    /**
     * Generate the data object which is used to build template layout
     */
    protected getRenderContext(): QuoteTemplateData {
        return {
            quoteArray: this.#quotes.map((quote) => {
                return {
                    author: quote.author,
                    background_link: quote.background_link,
                    id: quote.id,
                    title: quote.title,
                    quote: quote.quote,
                };
            }),
            widget: { id: this.id },
            metadata: {
                landscape: this.#layoutType === QuoteLayoutType.LANDSCAPE,
                fontResize: false,
                fontProperties: {
                    checkAllChildren: false,
                    checkWidthOverflow: false,
                    ellipsis: false,
                    maxSize: this.getMaximumFontSize(),
                    maxTextHeight: 0,
                    minSize: this.getMinimumFontSize(),
                },
            },
        };
    }

    /**
     * Triggered before fade out occurs
     */
    beforeFadeOut(): Partial<FadeOutCommand> {
        return {
            // Do not cancel the fade in/out even if it is the same widget
            cancelFade: false,
        };
    }

    /**
     * Determines slots to send to the Timeline.
     * @param numSlots - maximum number of slots
     * @param maxDuration - time left in loop
     */
    querySlots(numSlots: number, maxDuration: number): QuoteWidgetTimelineSlot[] {
        const slots: QuoteWidgetTimelineSlot[] = [];

        this.hasContentToPlay = true;
        if (this.#quotes.length === 0) {
            this.hasContentToPlay = false;
            // defaulting to [] as error
            return slots;
        }

        for (let i = 0; i < numSlots; ++i) {
            if (this.duration <= maxDuration) {
                const quote = this.#quotes[this.#currentQuoteIndex];

                const testSlot: QuoteWidgetTimelineSlotObject = {
                    quote: {
                        author: quote.author || '',
                        background_link: quote.background_link,
                        id: quote.id,
                        quote: quote.quote || '',
                        template: parseInt(this.config.template),
                        title: quote.title || '',
                        type: this.type,
                    },
                };

                const slot = this.generateSlot(this.duration, undefined, testSlot);
                slots.push(slot);

                maxDuration -= this.duration;
            } else {
                break;
            }

            this.#currentQuoteIndex++;
            if (this.#currentQuoteIndex > this.#quotes.length - 1) {
                this.#currentQuoteIndex = 0;
            }
        }

        return slots;
    }

    /**
     * Determines slots to send to the Timeline.LinearTimeline
     * @param timeBased - use this.minimumLoopTime to determine
     *   how many slots to give the timeline
     */
    querySlotsLinear(timeBased: boolean): QuoteWidgetTimelineSlot[] {
        const slots: QuoteWidgetTimelineSlot[] = [];

        this.hasContentToPlay = true;
        if (this.#quotes.length === 0) {
            this.hasContentToPlay = false;
            // defaulting to [] as error
            return [];
        }

        if (timeBased) {
            let usedTime = 0;

            do {
                const quote = this.#quotes[this.contentStartIndex];
                const testSlot: QuoteWidgetTimelineSlotObject = {
                    quote: {
                        author: quote.author || '',
                        background_link: quote.background_link,
                        id: quote.id,
                        quote: quote.quote || '',
                        template: parseInt(this.config.template),
                        title: quote.title || '',
                        type: this.type,
                    },
                };
                const slot = this.generateSlot(this.duration, undefined, testSlot);
                slots.push(slot);

                // If the next item is undefined, set contentStartIndex to 0
                const nextIndex = this.contentStartIndex + 1;
                this.contentStartIndex = this.#quotes[nextIndex] !== undefined ? nextIndex : 0;
                usedTime += this.duration;
            } while (usedTime < this.minimumLoopTime);
        } else {
            this.#quotes.forEach((quote) => {
                const testSlot: QuoteWidgetTimelineSlotObject = {
                    quote: {
                        author: quote.author || '',
                        background_link: quote.background_link,
                        id: quote.id,
                        quote: quote.quote || '',
                        template: parseInt(this.config.template),
                        title: quote.title || '',
                        type: this.type,
                    },
                };
                const slot = this.generateSlot(this.duration, undefined, testSlot);
                slots.push(slot);

                this.#currentQuoteIndex++;
                if (this.#currentQuoteIndex > this.#quotes.length - 1) {
                    this.#currentQuoteIndex = 0;
                }
            });
        }

        return slots;
    }

    /**
     * Returns layout type
     */
    getLayoutType(): QuoteLayoutType {
        if (this.wrapperElement.clientHeight > this.wrapperElement.clientWidth) {
            return QuoteLayoutType.PORTRAIT;
        }

        return QuoteLayoutType.LANDSCAPE;
    }

    /**
     * Uses linear equation to find the minimum possible font size.
     * @returns fontsize - Minimum font size for the widget text
     */
    getMinimumFontSize(): number {
        let fontsize = 8;
        if (this.config.width && this.config.height) {
            if (this.getLayoutType() === QuoteLayoutType.PORTRAIT) {
                fontsize = Math.round(
                    ((parseInt(this.config.width) + 209) / 54) *
                        (parseInt(this.config.height) / parseInt(this.config.width))
                );
            } else {
                fontsize = Math.round((parseInt(this.config.width) + 209) / 54);
            }
        }

        return fontsize;
    }

    /**
     * Uses linear equation to find the maximum possible font size. y = 26x + 24,
     * where, y is screen width and x is font-size, might need improving
     * @returns Maximum font size for the widget text
     */
    getMaximumFontSize(): number {
        let fontsize = 8;
        //the equation of the trend line y = 26x + 24, where, y is screen width and
        //x is font-size
        if (this.config.width && this.config.height) {
            if (this.getLayoutType() === QuoteLayoutType.PORTRAIT) {
                fontsize =
                    Math.round(
                        ((parseInt(this.config.width) - 24) / 26 + 0.00001) *
                            (parseInt(this.config.height) / parseInt(this.config.width))
                    ) + 4;
            } else {
                fontsize = Math.round((parseInt(this.config.width) - 24) / 26 + 0.00001) + 4;
            }
        }

        return fontsize;
    }
}
